import { all, call, delay, put, takeEvery } from 'redux-saga/effects'
import {
  CreateUpdateAPIAccessType,
  CREATE_API_ACCESS,
  DeleteAPIAccessType,
  DELETE_API_ACCESS,
  LoadAPIAccessListType,
  LOAD_API_ACCESS,
  UPDATE_API_ACCESS,
  loadAPIAccessList,
  CREATE_API_KEY,
  UPDATE_API_KEY,
  CreateUpdateAPIKeyType,
  DELETE_API_KEYS,
  DeleteAPIKeysType
} from '../actions/apiAccess'

import { addError } from '../actions/errors'
import {
  getAccounts,
  postAccount,
  patchAccount,
  deleteAccounts,
  postAPIKey,
  patchAPIKey,
  getApiKeys,
  deleteAPIKeys,
  postUser,
  getUsers,
  delUser
} from '../apis/admin'
import IAPIAccess from '../model/IAPIAccess'
import {
  setApiAccessLoadingState,
  setAPIAccesssList,
  deleteAPIAccessList,
  addAPIAccessList
} from '../reducers/apiAccess'
import {
  addAPIKeyList,
  deleteAPIKeyList,
  setAPIKeysList,
  setLastAddedAPIKey
} from '../reducers/apiKeys'
import { flatten } from 'lodash'
import { ROLE_TYPES_EXT } from '../helpers/project'

export default function* watchAdmin() {
  yield takeEvery(LOAD_API_ACCESS, fetchApiAccess)
  yield takeEvery(CREATE_API_ACCESS, createApiAccess)
  yield takeEvery(UPDATE_API_ACCESS, updateApiAccess)
  yield takeEvery(DELETE_API_ACCESS, deleteApiAccess)
  yield takeEvery(CREATE_API_KEY, createApiKey)
  yield takeEvery(UPDATE_API_KEY, updateApiKey)
  yield takeEvery(DELETE_API_KEYS, deleteApiKeys)
}

export function* fetchApiAccess(action: LoadAPIAccessListType) {
  if (action.tenantId === '') {
    return
  }

  try {
    yield put(setApiAccessLoadingState({ loadingState: true }))

    const usersRoles = yield call(getUsers, action.tenantId)
    const response = yield call(getAccounts, action.tenantId)

    const accountsWithRoles = response.data.map(account => {
      const foundRole = usersRoles.data.find(
        user => user.userId === account.accountId
      )?.role

      if (foundRole) {
        account.role = foundRole
      }

      return account
    })

    // Dispatched unconditionally, empty list included: skipping it left the
    // previous folder's rows on screen when the one just loaded has no
    // accounts. Loading stays true (set above) until the keys are fetched
    // below, so the list is never reported as fully loaded while incomplete.
    yield put(setAPIAccesssList({ apiAccess: accountsWithRoles }))

    const keyList = yield all(
      accountsWithRoles.map((account: IAPIAccess) =>
        call(getApiKeys, action.tenantId, account)
      )
    )

    yield put(
      setAPIKeysList({ apiKeys: flatten(keyList.map(item => item.data)) })
    )
    yield put(setApiAccessLoadingState({ loadingState: false }))
  } catch (error) {
    yield put(addError(LOAD_API_ACCESS, error))
  }
}

export function* createApiAccess(action: CreateUpdateAPIAccessType) {
  try {
    const response = yield call(
      postAccount,
      action.tenantId as any,
      action.apiAccess as any
    )

    if (action.apiAccess.role !== ROLE_TYPES_EXT.NONE) {
      yield call(
        postUser,
        {
          userId: response.accountId,
          role: action.apiAccess.role,
          principalType: 'OpenAPIAccount'
        } as any,
        action.tenantId as any
      )
    }

    yield put(loadAPIAccessList(action.tenantId))
  } catch (error) {
    yield put(addError(CREATE_API_ACCESS, error))
  }
}

export function* updateApiAccess(action: CreateUpdateAPIAccessType) {
  try {
    const { accountId, role } = action.apiAccess

    yield call(patchAccount, action.tenantId, action.apiAccess)

    // IAM PATCH triggers an async cascade to the metadata service; the DELETE
    // can arrive before that cascade completes and return 500. Retry once after
    // a short delay if that happens.
    try {
      yield call(delUser, accountId as string, action.tenantId)
    } catch {
      yield delay(2000)
      yield call(delUser, accountId as string, action.tenantId)
    }

    if (role !== ROLE_TYPES_EXT.NONE) {
      yield call(
        postUser,
        { userId: accountId, role, principalType: 'OpenAPIAccount' } as any,
        action.tenantId as any
      )
    }

    yield put(loadAPIAccessList(action.tenantId))
  } catch (error) {
    yield put(addError(UPDATE_API_ACCESS, error))
  }
}

export function* deleteApiAccess(action: DeleteAPIAccessType) {
  try {
    yield put(deleteAPIAccessList({ apiAccessList: action.apiAccessList }))
    yield call(deleteAccounts, action.tenantId, action.apiAccessList)
  } catch (error) {
    yield put(addAPIAccessList({ apiAccessList: action.apiAccessList }))
    yield put(addError(CREATE_API_ACCESS, error))
  }
}

export function* createApiKey(action: CreateUpdateAPIKeyType) {
  try {
    const result = yield call(
      postAPIKey,
      action.tenantId,
      action.apiAccess,
      action.apiKey
    )
    yield put(setLastAddedAPIKey({ apiKey: result }))
    yield put(loadAPIAccessList(action.tenantId))
  } catch (error) {
    yield put(addError(CREATE_API_ACCESS, error))
  }
}

export function* updateApiKey(action: CreateUpdateAPIKeyType) {
  try {
    yield call(patchAPIKey, action.tenantId, action.apiAccess, action.apiKey)
    yield put(loadAPIAccessList(action.tenantId))
  } catch (error) {
    yield put(addError(CREATE_API_ACCESS, error))
  }
}

export function* deleteApiKeys(action: DeleteAPIKeysType) {
  try {
    yield put(deleteAPIKeyList({ apiKeys: action.apiKeyList }))
    yield call(deleteAPIKeys, action.tenantId, action.apiKeyList)
  } catch (error) {
    yield put(
      addAPIKeyList({
        apiKeys: action.apiKeyList
      })
    )
    yield put(addError(CREATE_API_ACCESS, error))
  }
}
